import React, { useState, useEffect, useRef } from 'react';
import { Tag as TagIcon, Plus, X } from 'lucide-react';
import { TagsService, TicketsService } from '@/services';
import { Tag } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import toast from 'react-hot-toast';

interface TagsManagerProps {
  ticketId: number;
  tagsIniciais?: Tag[];
  onTagsChange?: (tags: Tag[]) => void;
  readOnly?: boolean;
}

export default function TagsManager({
  ticketId,
  tagsIniciais = [],
  onTagsChange,
  readOnly = false,
}: TagsManagerProps) {
  const { usuario } = useAuth();
  const [tags, setTags] = useState<Tag[]>(tagsIniciais);
  const [todasTags, setTodasTags] = useState<Tag[]>([]);
  const [showAdicionar, setShowAdicionar] = useState(false);
  const [showCriarTag, setShowCriarTag] = useState(false);
  const [tagSelecionada, setTagSelecionada] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [novaTagNome, setNovaTagNome] = useState('');
  const [novaTagCor, setNovaTagCor] = useState('#3B82F6');
  const [novaTagDescricao, setNovaTagDescricao] = useState('');
  const [isCriandoTag, setIsCriandoTag] = useState(false);
  const hasLoadedRef = useRef(false);
  const lastTicketIdRef = useRef<number | null>(null);

  useEffect(() => {
    loadTodasTags();
  }, []);

  useEffect(() => {
    // Se o ticketId mudou, resetar o flag
    if (lastTicketIdRef.current !== ticketId) {
      hasLoadedRef.current = false;
      lastTicketIdRef.current = ticketId;
    }

    // Se temos tags iniciais, usar elas
    if (tagsIniciais && tagsIniciais.length > 0) {
      setTags(tagsIniciais);
      hasLoadedRef.current = true;
    } else if (ticketId && !hasLoadedRef.current) {
      // Só carregar se ainda não carregou para este ticket
      loadTagsDoTicket();
      hasLoadedRef.current = true;
    }
  }, [ticketId]);

  // Atualizar tags quando tagsIniciais mudar (mas só se realmente mudou)
  useEffect(() => {
    if (tagsIniciais && tagsIniciais.length > 0) {
      setTags(prevTags => {
        const currentTagsIds = prevTags.map(t => t.id).sort().join(',');
        const newTagsIds = tagsIniciais.map(t => t.id).sort().join(',');
        if (currentTagsIds !== newTagsIds) {
          return tagsIniciais;
        }
        return prevTags;
      });
    }
  }, [tagsIniciais]);

  const loadTodasTags = async () => {
    try {
      const data = await TagsService.getTags();
      setTodasTags(data);
    } catch (error: any) {
      console.error('Erro ao carregar tags:', error);
      toast.error('Erro ao carregar tags');
    }
  };

  const loadTagsDoTicket = async () => {
    try {
      const data = await TicketsService.getTagsDoTicket(ticketId);
      // Só atualizar se os dados realmente mudaram
      const tagsChanged = JSON.stringify(data) !== JSON.stringify(tags);
      if (tagsChanged) {
        setTags(data);
        onTagsChange?.(data);
      }
    } catch (error: any) {
      console.error('Erro ao carregar tags do ticket:', error);
    }
  };

  const handleAdicionarTag = async () => {
    if (!tagSelecionada) return;

    try {
      setIsLoading(true);
      const response = await TicketsService.adicionarTagsAoTicket(ticketId, [tagSelecionada]);
      setTags(response.tags);
      onTagsChange?.(response.tags);
      setShowAdicionar(false);
      setTagSelecionada(null);
      toast.success('Tag adicionada com sucesso');
    } catch (error: any) {
      console.error('Erro ao adicionar tag:', error);
      toast.error(error.response?.data?.error || 'Erro ao adicionar tag');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoverTag = async (tagId: number) => {
    try {
      setIsLoading(true);
      const response = await TicketsService.removerTagsDoTicket(ticketId, [tagId]);
      setTags(response.tags);
      onTagsChange?.(response.tags);
      toast.success('Tag removida com sucesso');
    } catch (error: any) {
      console.error('Erro ao remover tag:', error);
      toast.error(error.response?.data?.error || 'Erro ao remover tag');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCriarTag = async () => {
    if (!novaTagNome.trim()) {
      toast.error('O nome da tag é obrigatório');
      return;
    }

    try {
      setIsCriandoTag(true);
      const novaTag = await TagsService.createTag({
        nome: novaTagNome.trim(),
        cor: novaTagCor,
        descricao: novaTagDescricao.trim() || undefined,
      });
      
      // Recarregar lista de tags
      await loadTodasTags();
      
      // Adicionar a nova tag ao ticket automaticamente
      const response = await TicketsService.adicionarTagsAoTicket(ticketId, [novaTag.id]);
      setTags(response.tags);
      onTagsChange?.(response.tags);
      
      // Limpar formulário
      setNovaTagNome('');
      setNovaTagCor('#3B82F6');
      setNovaTagDescricao('');
      setShowCriarTag(false);
      
      toast.success('Tag criada e adicionada ao ticket com sucesso');
    } catch (error: any) {
      console.error('Erro ao criar tag:', error);
      toast.error(error.response?.data?.error || 'Erro ao criar tag');
    } finally {
      setIsCriandoTag(false);
    }
  };

  const tagsDisponiveis = todasTags.filter(
    (tag) => !tags.some((t) => t.id === tag.id)
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TagIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">
            Etiquetas
          </h3>
        </div>
        {!readOnly && !showAdicionar && !showCriarTag && (
          <button
            onClick={() => setShowAdicionar(true)}
            className="inline-flex items-center justify-center p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            title="Adicionar etiqueta existente"
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {tags.map((tag) => (
          <span
            key={tag.id}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium"
            style={{
              backgroundColor: `${tag.cor}20`,
              color: tag.cor,
              border: `1px solid ${tag.cor}40`,
            }}
          >
            <TagIcon className="w-3 h-3" />
            {tag.nome}
            {!readOnly && (
              <button
                onClick={() => handleRemoverTag(tag.id)}
                disabled={isLoading}
                className="ml-1 hover:opacity-70 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </span>
        ))}

        {!readOnly && (
          <>
            {showAdicionar && (
              <div className="w-full p-3 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-lg border border-gray-200/50 dark:border-gray-700/50 space-y-3">
                <div className="flex items-center gap-2">
                  <select
                    value={tagSelecionada || ''}
                    onChange={(e) => setTagSelecionada(Number(e.target.value))}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
                    disabled={isLoading}
                  >
                    <option value="">Selecione uma etiqueta</option>
                    {tagsDisponiveis.map((tag) => (
                      <option key={tag.id} value={tag.id}>
                        {tag.nome}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => {
                      setShowAdicionar(false);
                      setShowCriarTag(true);
                    }}
                    className="inline-flex items-center justify-center p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    title="Criar nova etiqueta"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      setShowAdicionar(false);
                      setTagSelecionada(null);
                    }}
                    className="inline-flex items-center justify-center p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleAdicionarTag}
                    disabled={!tagSelecionada || isLoading}
                    className="flex-1 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                  >
                    {isLoading ? 'Adicionando...' : 'Adicionar'}
                  </button>
                </div>
              </div>
            )}
            {showCriarTag && (
              <div className="w-full p-3 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-lg border border-gray-200/50 dark:border-gray-700/50 space-y-3">
                <input
                  type="text"
                  placeholder="Nome da etiqueta"
                  value={novaTagNome}
                  onChange={(e) => setNovaTagNome(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 text-sm focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
                  disabled={isCriandoTag}
                />
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={novaTagCor}
                    onChange={(e) => setNovaTagCor(e.target.value)}
                    className="w-12 h-10 rounded-lg border border-gray-300 dark:border-gray-600 cursor-pointer"
                    disabled={isCriandoTag}
                  />
                  <input
                    type="text"
                    placeholder="Descrição (opcional)"
                    value={novaTagDescricao}
                    onChange={(e) => setNovaTagDescricao(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 text-sm focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
                    disabled={isCriandoTag}
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleCriarTag}
                    disabled={!novaTagNome.trim() || isCriandoTag}
                    className="flex-1 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                  >
                    {isCriandoTag ? 'Criando...' : 'Criar e Adicionar'}
                  </button>
                  <button
                    onClick={() => {
                      setShowCriarTag(false);
                      setNovaTagNome('');
                      setNovaTagCor('#3B82F6');
                      setNovaTagDescricao('');
                    }}
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

